import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, RefreshCw, Send, TriangleAlert, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { EmployeeAvatar } from "@/components/timeclock/EmployeeAvatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, fortnoxApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatStamp, localDate } from "#shared/timeclock";
import type { Employee, VacationRequest, VacationStatus } from "#shared/types";
import { formatDateRange, VACATION_LABELS, VACATION_STATUS_LABELS } from "#shared/vacation";

const STATUS_STYLES: Record<VacationStatus, string> = {
  vantande: "bg-warning/20 text-warning-foreground",
  godkand: "bg-success/15 text-success",
  nekad: "bg-destructive/12 text-destructive",
  aterkallad: "bg-muted text-muted-foreground",
};

const FILTERS: { value: VacationStatus | "alla"; label: string }[] = [
  { value: "vantande", label: "Väntande" },
  { value: "godkand", label: "Godkända" },
  { value: "nekad", label: "Nekade" },
  { value: "alla", label: "Alla" },
];

type Decision = { request: VacationRequest; status: "godkand" | "nekad" };

export function VacationPanel() {
  const queryClient = useQueryClient();
  const year = new Date().getFullYear();

  const [filter, setFilter] = useState<VacationStatus | "alla">("vantande");
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState("");
  const [exportFrom, setExportFrom] = useState(`${year}-01-01`);
  const [exportTo, setExportTo] = useState(localDate());

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["vacation", "admin", filter],
    queryFn: () => adminApi.vacation(filter === "alla" ? undefined : filter),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: adminApi.employees,
  });

  const { data: balances = [] } = useQuery({
    queryKey: ["vacation", "balances", year],
    queryFn: () => adminApi.vacationBalances(year),
  });

  const employeeById = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const employee of employees) map.set(employee.id, employee);
    return map;
  }, [employees]);

  const balanceById = useMemo(() => {
    const map = new Map<string, (typeof balances)[number]>();
    for (const balance of balances) map.set(balance.employee_id, balance);
    return map;
  }, [balances]);

  const { data: pending } = useQuery({
    queryKey: ["export", "pending"],
    queryFn: adminApi.pendingExport,
  });

  const refreshBalances = useMutation({
    mutationFn: fortnoxApi.refreshBalances,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["vacation", "balances"] });
      queryClient.invalidateQueries({ queryKey: ["balances"] });
      toast.success(`Saldon hämtade för ${result.length} anställd(a)`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const decide = useMutation({
    mutationFn: ({ request, status }: Decision) =>
      adminApi.decideVacation(request.id, status, note.trim() || null),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["vacation"] });
      setDecision(null);
      setNote("");
      toast.success(variables.status === "godkand" ? "Ledigheten är godkänd" : "Ansökan är nekad");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const exportLeave = useMutation({
    mutationFn: () => adminApi.exportVacation(exportFrom, exportTo),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["vacation"] });
      queryClient.invalidateQueries({ queryKey: ["export", "pending"] });
      if (result.failures.length > 0) {
        toast.error(
          `${result.days_sent} dag(ar) skickade, ${result.failures.length} misslyckades: ${result.failures[0].message}`,
        );
      } else {
        toast.success(
          `${result.days_sent} ledighetsdag(ar) i ${result.sent.length} ansökning(ar) skickade till Fortnox`,
        );
      }
      if (result.skipped_missing_number.length > 0) {
        toast.warning(
          `Hoppade över (saknar anställningsnummer): ${result.skipped_missing_number.join(", ")}`,
        );
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const pendingCount = requests.filter((request) => request.status === "vantande").length;

  return (
    <div className="space-y-5">
      <div className="surface p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Ledighetsansökningar</h2>
            <p className="text-sm text-muted-foreground">
              {filter === "vantande" && pendingCount > 0
                ? `${pendingCount} ansökning(ar) väntar på svar.`
                : "Godkänn eller neka personalens ansökningar om ledighet."}
            </p>
          </div>

          <div className="flex gap-1 rounded-full bg-muted p-1">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                  filter === option.value
                    ? "bg-card shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <p className="py-10 text-center text-muted-foreground">Hämtar…</p>
        ) : requests.length === 0 ? (
          <p className="py-10 text-center text-muted-foreground">Inga ansökningar att visa här.</p>
        ) : (
          <ul className="mt-5 divide-y">
            {requests.map((request) => {
              const employee = employeeById.get(request.employee_id);
              const balance = balanceById.get(request.employee_id);
              return (
                <li key={request.id} className="flex flex-wrap items-center gap-4 py-3.5">
                  <EmployeeAvatar
                    name={request.employee_name}
                    photoUrl={employee?.photo_url}
                    className="size-11 text-2xl"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {request.employee_name}
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        {VACATION_LABELS[request.kind]}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatDateRange(request.start_date, request.end_date)} · {request.days}{" "}
                      {request.days === 1 ? "arbetsdag" : "arbetsdagar"}
                      {request.kind === "semester" && balance
                        ? ` · ${balance.days_left} av ${balance.days_per_year} dagar kvar ${year}`
                        : ""}
                    </p>
                    {request.message && (
                      <p className="mt-1 text-sm italic text-muted-foreground">
                        ”{request.message}”
                      </p>
                    )}
                    {request.decided_by_name && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {VACATION_STATUS_LABELS[request.status]} av {request.decided_by_name}
                        {request.decision_note ? ` · ${request.decision_note}` : ""}
                      </p>
                    )}
                  </div>

                  {request.status === "vantande" ? (
                    <div className="flex gap-2">
                      <Button
                        className="rounded-full bg-success text-success-foreground hover:bg-success/90"
                        onClick={() => {
                          setNote("");
                          setDecision({ request, status: "godkand" });
                        }}
                      >
                        <Check className="size-4" /> Godkänn
                      </Button>
                      <Button
                        variant="ghost"
                        className="rounded-full text-destructive"
                        onClick={() => {
                          setNote("");
                          setDecision({ request, status: "nekad" });
                        }}
                      >
                        <X className="size-4" /> Neka
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={cn(
                          "rounded-full px-3 py-1 text-xs font-semibold",
                          STATUS_STYLES[request.status],
                        )}
                      >
                        {VACATION_STATUS_LABELS[request.status]}
                      </span>
                      {/* Godkänd betyder inte skickad — det är två olika steg. */}
                      {request.status === "godkand" && (
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            request.exported_at
                              ? "bg-success/15 text-success"
                              : "bg-warning/20 text-warning-foreground",
                          )}
                        >
                          {request.exported_at
                            ? `Skickad ${request.exported_at.slice(0, 10)}`
                            : "Ej skickad till Fortnox"}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="surface p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Semestersaldon</h2>
            <p className="text-sm text-muted-foreground">
              Hämtade från personalkortet i Fortnox, som räknar med intjänandeår, sparade dagar och
              uttag gjorda utanför den här appen.
            </p>
          </div>
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={refreshBalances.isPending}
            onClick={() => refreshBalances.mutate()}
          >
            <RefreshCw className="size-4" />
            {refreshBalances.isPending ? "Hämtar…" : "Uppdatera"}
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase">
                <th className="py-2 pr-3 font-medium">Anställd</th>
                <th className="py-2 pr-3 font-medium">Betalda i år</th>
                <th className="py-2 pr-3 font-medium">Sparade</th>
                <th className="py-2 pr-3 font-medium">Uttagna</th>
                <th className="py-2 pr-3 font-medium">Kvar</th>
                <th className="py-2 pr-3 font-medium">Hämtat</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {employees.map((employee) => {
                const fortnox = balanceById.get(employee.id)?.fortnox ?? null;
                const local = balanceById.get(employee.id);
                return (
                  <tr key={employee.id}>
                    <td className="py-2.5 pr-3 font-medium">{employee.name}</td>
                    {fortnox ? (
                      <>
                        <td className="tabular py-2.5 pr-3">{fortnox.paid_days ?? "—"}</td>
                        <td className="tabular py-2.5 pr-3">{fortnox.saved_days ?? "—"}</td>
                        <td className="tabular py-2.5 pr-3">
                          {fortnox.used_days ?? "—"}
                          {fortnox.pending_days ? ` (+${fortnox.pending_days} väntande)` : ""}
                        </td>
                        <td className="tabular py-2.5 pr-3 font-semibold">
                          {fortnox.days_left ?? "—"}
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                          {formatStamp(fortnox.fetched_at)}
                        </td>
                      </>
                    ) : (
                      <td colSpan={5} className="py-2.5 pr-3 text-muted-foreground">
                        Inget saldo från Fortnox
                        {employee.employee_number
                          ? " — tryck Uppdatera"
                          : " — saknar anställningsnummer"}
                        {local ? `. Räknat här: ${local.days_left} av ${local.days_per_year}.` : ""}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="surface p-5 sm:p-6">
        <h2 className="text-lg font-semibold">Skicka ledighet till Fortnox</h2>
        <p className="text-sm text-muted-foreground">
          Godkänd ledighet rapporteras som en rad per arbetsdag med orsakskoden för respektive typ
          (SEM, FLX, TJL eller FPE). Ansökningar som redan skickats hoppas över.
        </p>

        {pending && pending.leave.count > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-warning/15 px-4 py-3 text-sm">
            <p className="flex items-center gap-2">
              <TriangleAlert className="size-4 shrink-0 text-warning-foreground" />
              <span>
                <strong>{pending.leave.count} ledighetsdag(ar)</strong> väntar på att skickas, från{" "}
                {pending.leave.oldest} till {pending.leave.newest}.
              </span>
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              onClick={() => {
                setExportFrom(pending.leave.oldest!);
                setExportTo(pending.leave.newest!);
              }}
            >
              Välj hela perioden
            </Button>
          </div>
        ) : pending ? (
          <p className="mt-4 text-sm text-success">All godkänd ledighet är skickad.</p>
        ) : null}

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="leave-from">Från</Label>
            <Input
              id="leave-from"
              type="date"
              value={exportFrom}
              onChange={(event) => setExportFrom(event.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="leave-to">Till</Label>
            <Input
              id="leave-to"
              type="date"
              value={exportTo}
              onChange={(event) => setExportTo(event.target.value)}
              className="w-40"
            />
          </div>
          <Button
            className="rounded-full"
            disabled={exportLeave.isPending}
            onClick={() => exportLeave.mutate()}
          >
            <Send className="size-4" />
            {exportLeave.isPending ? "Skickar…" : "Skicka godkänd ledighet"}
          </Button>
        </div>
      </div>

      <Dialog open={!!decision} onOpenChange={(open) => !open && setDecision(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>
              {decision?.status === "godkand" ? "Godkänn ledighet" : "Neka ansökan"}
            </DialogTitle>
          </DialogHeader>

          {decision && (
            <div className="space-y-4">
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm">
                <strong>{decision.request.employee_name}</strong> —{" "}
                {VACATION_LABELS[decision.request.kind].toLowerCase()}{" "}
                {formatDateRange(decision.request.start_date, decision.request.end_date)} (
                {decision.request.days} {decision.request.days === 1 ? "arbetsdag" : "arbetsdagar"})
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="decision-note">
                  Kommentar {decision.status === "nekad" ? "(rekommenderas)" : "(frivilligt)"}
                </Label>
                <Textarea
                  id="decision-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={
                    decision.status === "nekad"
                      ? "T.ex. för många lediga samtidigt den perioden"
                      : "Syns för den anställda i panelen"
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setDecision(null)}>
              Avbryt
            </Button>
            <Button
              className={cn(
                "rounded-full",
                decision?.status === "godkand"
                  ? "bg-success text-success-foreground hover:bg-success/90"
                  : "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              )}
              disabled={decide.isPending}
              onClick={() => decision && decide.mutate(decision)}
            >
              {decide.isPending ? "Sparar…" : decision?.status === "godkand" ? "Godkänn" : "Neka"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
